import { useRef, useState } from 'react';
import { useAppStore } from '../../store/appStore';
import { ChatConversation } from './ChatConversation';
import { ChatResizeHandle } from './ChatResizeHandle';
import { darkTheme, FONT } from '../../theme/tokens';

const t = darkTheme;

// Width of the collapsed rail — a slim, always-visible re-open handle (it gives
// back ~all of the panel's width while keeping the chat one click away).
const RAIL_WIDTH = 44;

interface ChatPanelProps {
  catalogId: string;
}

/**
 * Canvas P3 (SC-2659) — the permanent chat panel: the right column of the
 * workspace frame (`canvas ∣ ChatPanel`). It's always present (no open/close gate)
 * and in-flow — a real flex column the canvas sits beside rather than an overlay the
 * canvas reflows under. It hosts the `ChatConversation`
 * and adds the collapse affordance (collapse to a slim, labelled rail to reclaim
 * canvas width — the mockup's `toggleChat`, minus the not-yet-built top bar) and
 * the in-flow resize geometry (the handle measures the panel's own right edge, not
 * the viewport). Collapse is a persisted pref (`appStore.chatCollapsed`), default
 * expanded (locked decision #4).
 */
export function ChatPanel({ catalogId }: ChatPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const dockWidth = useAppStore((s) => s.dockWidth);
  const dockResizing = useAppStore((s) => s.dockResizing);
  const collapsed = useAppStore((s) => s.chatCollapsed);
  const setChatCollapsed = useAppStore((s) => s.setChatCollapsed);
  // Hover highlights so both affordances read as buttons (inline styles can't do
  // `:hover`) — the discoverability the bare chevron lacked.
  const [railHover, setRailHover] = useState(false);
  const [collapseHover, setCollapseHover] = useState(false);

  return (
    <aside
      ref={panelRef}
      aria-label="EKOM chat"
      style={{
        position: 'relative',
        flexShrink: 0,
        width: collapsed ? RAIL_WIDTH : dockWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: t.bg,
        borderLeft: `1px solid ${t.border}`,
        fontFamily: FONT,
        // Suspend the width ease during a drag so the edge tracks the cursor 1:1.
        transition: dockResizing ? 'none' : 'width 0.18s ease',
      }}
    >
      {collapsed ? (
        // The whole rail is the re-open target: ✦ + a vertical "CHAT" label + a
        // left-pointing chevron (expands back into view), full-height so it's an
        // easy, obvious hit.
        <button
          type="button"
          onClick={() => setChatCollapsed(false)}
          aria-label="Expand chat"
          aria-expanded={false}
          title="Expand chat"
          onMouseEnter={() => setRailHover(true)}
          onMouseLeave={() => setRailHover(false)}
          style={{
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            padding: '16px 0',
            background: railHover ? t.surfaceAlt : 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: FONT,
            transition: 'background 0.12s',
          }}
        >
          <span aria-hidden style={{ fontSize: 15, color: t.blueText }}>
            ✦
          </span>
          <span
            aria-hidden
            style={{
              writingMode: 'vertical-rl',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: t.text3,
            }}
          >
            Chat
          </span>
          <span aria-hidden style={{ fontSize: 14, color: t.text3 }}>
            «
          </span>
        </button>
      ) : (
        <>
          {/* Handle measures the panel's own right edge (in-flow), not the viewport. */}
          <ChatResizeHandle
            measureRightEdge={() =>
              panelRef.current?.getBoundingClientRect().right ?? window.innerWidth
            }
          />
          <ChatConversation
            catalogId={catalogId}
            // Far left of the header — clear of the "New chat" cluster on the right
            // so the collapse chevron can't read as part of that action.
            leading={
              <button
                type="button"
                onClick={() => setChatCollapsed(true)}
                aria-label="Collapse chat"
                aria-expanded
                title="Collapse chat to reclaim space"
                onMouseEnter={() => setCollapseHover(true)}
                onMouseLeave={() => setCollapseHover(false)}
                style={{
                  background: collapseHover ? t.surfaceAlt : 'none',
                  border: 'none',
                  color: t.text3,
                  fontSize: 16,
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: 6,
                  fontFamily: FONT,
                  transition: 'background 0.12s',
                }}
              >
                <span aria-hidden>»</span>
              </button>
            }
          />
        </>
      )}
    </aside>
  );
}
